/**
 * Validation module for User component
 * Provides field-specific validation functions
 */

import * as validate from "./helper";
import * as intrfce from "./Interfaces";
import { validateBlockedTerritory } from "./utils/blockedTerritories";

/**
 * Validation context interface
 */
export interface ValidationContext {
  locale: string;
  userType?: string;
  stateDropdown: boolean;
  formBillingData: intrfce.iBillingFormData;
  formBillingAddress: intrfce.iAddressFormData;
  formShippingAddress: intrfce.iAddressFormData;
  formTaxData: intrfce.iTaxFormData;
  shippingOptions: intrfce.iShippingDetails;
  t: (key: string) => string;
}

/**
 * Validates name fields (first name, last name)
 */
export const validateName = (
  value: string,
  isEmpty: string,
  isInvalid: string
): string => {
  if (!value || value.trim() === "") {
    return isEmpty;
  }
  if (!validate.validateInput(value)) {
    return isInvalid;
  }
  return "";
};

/**
 * Validates email field
 */
export const validateEmailField = (
  value: string,
  isEmpty: string,
  isInvalid: string
): string => {
  if (!value || value.trim() === "") {
    return isEmpty;
  }
  if (!validate.validateEmail(value)) {
    return isInvalid;
  }
  return "";
};

/**
 * Validates email verification field
 */
export const validateEmailVerification = (
  value: string,
  emailValue: string,
  isEmpty: string,
  isInvalid: string,
  isMismatch: string
): string => {
  if (!value || value.trim() === "") {
    return isEmpty;
  }
  if (!validate.validateEmail(value)) {
    return isInvalid;
  }
  if (value !== emailValue) {
    return isMismatch;
  }
  return "";
};

/**
 * Validates contact number
 */
export const validateContact = (
  value: string,
  isEmpty: string,
  isInvalid: string
): string => {
  if (!value || value.trim() === "") {
    return isEmpty;
  }
  if (!validate.validateContactNumber(value)) {
    return isInvalid;
  }
  return "";
};

/**
 * Validates postal/zip code
 */
export const validatePostalCode = (
  value: string,
  locale: string,
  countryValue: string,
  isEmpty: string,
  isInvalid: string,
  isBlocked?: string
): string => {
  if (locale === 'en-hk') {
    return "";
  }
  if (!value || value.trim() === "") {
    return isEmpty;
  }
  
  // Check if the postal code is in a blocked territory (PRIORITY CHECK)
  const blockedTerritoryCheck = validateBlockedTerritory(value, countryValue, locale);
  if (blockedTerritoryCheck.isBlocked) {
    // Return the translated blocked territory message if provided, otherwise fallback
    return isBlocked || 'Sorry, this region is currently not supported.';
  }
  
  // Check postal code format only after territory check
  if (!validate.validatePostalCodes(value, locale, countryValue)) {
    return isInvalid;
  }
  return "";
};

/**
 * Validates address field
 */
export const validateAddressField = (
  value: string,
  locale: string,
  isEmpty: string,
  isInvalid: string,
  isPoBox: string,
  isTooShort: string,
  hasRepeatingLetters: string,
  hasTooManySpecialChars: string
): string => {
  if (!value || value.trim() === "") {
    return isEmpty;
  }
  
  // Check address line 1 specific validations
  const line1Validation = validate.validateAddressLine1(value);
  if (!line1Validation.isValid) {
    switch (line1Validation.error) {
      case 'ADDRESS_TOO_SHORT':
        return isTooShort;
      case 'ADDRESS_REPEATING_LETTERS':
        return hasRepeatingLetters;
      case 'ADDRESS_TOO_MANY_SPECIAL_CHARS':
        return hasTooManySpecialChars;
    }
  }
  
  if (!validate.validateStringOrIntegerAddress(value, locale)) {
    return isInvalid;
  }
  if (!validate.validateAddress(value)) {
    return isPoBox;
  }
  return "";
};

/**
 * Validates city field
 */
export const validateCityField = (
  value: string,
  locale: string,
  isEmpty: string,
  isInvalid: string
): string => {
  if (!value || value.trim() === "") {
    return isEmpty;
  }
  if (!validate.validateCity(value, locale)) {
    return isInvalid;
  }
  return "";
};

/**
 * Validates state field
 */
export const validateStateField = (
  value: string,
  stateDropdown: boolean,
  locale: string,
  isEmpty: string,
  isInvalid: string
): string => {
  if (stateDropdown && locale !== 'en-me') {
    if (!value || value.trim() === "") {
      return isEmpty;
    }
    if (!validate.validateInput(value)) {
      return isInvalid;
    }
  }
  return "";
};

/**
 * Validates company name (conditional based on VAT)
 */
export const validateCompanyName = (
  value: string,
  vatNo: string,
  isEmpty: string
): string => {
  if (vatNo && vatNo.trim() !== '') {
    if (!value || value.trim() === '') {
      return isEmpty;
    }
  }
  return '';
};

/**
 * Validates VAT number (conditional based on company name)
 */
export const validateVatNumber = (
  value: string,
  companyName: string,
  isEmpty: string
): string => {
  if (companyName && companyName.trim() !== '') {
    if (!value || value.trim() === '') {
      return isEmpty;
    }
  }
  return '';
};

/**
 * Main validation function that orchestrates all field validations
 */
export const validateField = (
  fieldName: string,
  value: string | {} | boolean,
  formType: "billing" | "shipping" | "",
  context: ValidationContext
): string => {
  const { t, locale, userType, stateDropdown, formBillingData, formBillingAddress, formShippingAddress, formTaxData, shippingOptions } = context;
  
  let error = "";

  // Common field validations
  switch (fieldName) {
    case "firstNameBilling":
      error = validateName(
        formBillingData.firstNameBilling,
        t("FIRST_NAME_EMPTY"),
        t("FIRST_NAME_INVALID")
      );
      break;
      
    case "lastNameBilling":
      error = validateName(
        formBillingData.lastNameBilling,
        t("LAST_NAME_EMPTY"),
        t("LAST_NAME_INVALID")
      );
      break;
      
    case "emailBilling":
      if (userType === "loggedIn") {
        error = validateEmailField(
          formBillingData.emailBilling,
          t("EMAIL_ADDRESS_EMPTY"),
          t("EMAIL_ADDRESS_INVALID")
        );
      }
      break;
      
    case "vemailBilling":
      if (userType !== "loggedIn") {
        error = validateEmailVerification(
          formBillingData.vemailBilling,
          formBillingData.emailBilling,
          t("EMAIL_ADDRESS_EMPTY"),
          t("EMAIL_ADDRESS_INVALID"),
          t("EMAIL_ADDRESS_MISMATCH")
        );
      }
      break;
      
    case "contactBilling":
      error = validateContact(
        formBillingData.contactBilling,
        t("CONTACT_NUMBER_EMPTY"),
        t("CONTACT_NUMBER_INVALID")
      );
      break;
      
    case "firstNameShiping":
      error = validateName(
        value as string,
        t("FIRST_NAME_EMPTY"),
        t("FIRST_NAME_INVALID")
      );
      break;
      
    case "lastNameShiping":
      error = validateName(
        value as string,
        t("LAST_NAME_EMPTY"),
        t("LAST_NAME_INVALID")
      );
      break;
      
    case "contactShiping":
      error = validateContact(
        value as string,
        t("CONTACT_NUMBER_EMPTY"),
        t("CONTACT_NUMBER_INVALID")
      );
      break;
      
    case "shippingOption":
      if (!shippingOptions.shippingOption) {
        error = t("SHIPPING_OPTION_REQUIRED");
      }
      break;
      
    case "pincode":
      error = validatePostalCode(
        value as string,
        locale,
        formType === "billing" ? formBillingAddress.countryValue : formShippingAddress.countryValue,
        t("ZIP_CODE_EMPTY"),
        t("ZIP_CODE_INVALID"),
        t("REGION_NOT_SUPPORTED")
      );
      break;
      
    case "address":
      error = validateAddressField(
        value as string,
        locale,
        t("ADDRESS_REQUIRED"),
        t("ADDRESS_INVALID"),
        t("ADDRESS_PO_BOX"),
        t("ADDRESS_TOO_SHORT"),
        t("ADDRESS_REPEATING_LETTERS"),
        t("ADDRESS_TOO_MANY_SPECIAL_CHARS")
      );
      break;
      
    case "cityValue":
      error = validateCityField(
        value as string,
        locale,
        t("CITY_EMPTY"),
        t("CITY_INVALID")
      );
      break;
      
    case "stateValue":
      error = validateStateField(
        value as string,
        stateDropdown,
        locale,
        t("STATE_EMPTY"),
        t("STATE_INVALID")
      );
      break;
      
    case "companyName":
      error = validateCompanyName(
        value as string,
        formTaxData.vatNo,
        t("COMPANY_NAME_REQUIRED")
      );
      break;
      
    case "vatNo":
      error = validateVatNumber(
        value as string,
        formTaxData.companyName,
        t("VAT_NUMBER_REQUIRED")
      );
      break;
  }
  
  // Form-type specific validations (billing)
  if (formType === "billing") {
    switch (fieldName) {
      case "cityBilling":
        error = validateCityField(
          value as string,
          locale,
          t("CITY_EMPTY"),
          t("CITY_INVALID")
        );
        break;
        
      case "stateNameBilling":
        error = validateName(
          value as string,
          t("STATE_EMPTY"),
          t("STATE_INVALID")
        );
        break;
        
      case "zipBilling":
        error = validatePostalCode(
          value as string,
          locale,
          formBillingAddress.countryValue,
          t("ZIP_CODE_EMPTY"),
          t("ZIP_CODE_INVALID"),
          t("REGION_NOT_SUPPORTED")
        );
        break;
        
      case "countryBilling":
        error = validateName(
          value as string,
          t("COUNTRY_REQUIRED"),
          t("COUNTRY_INVALID")
        );
        break;
        
      case "addressBilling":
        error = validateAddressField(
          value as string,
          locale,
          t("ADDRESS_REQUIRED"),
          t("ADDRESS_INVALID"),
          t("ADDRESS_PO_BOX"),
          t("ADDRESS_TOO_SHORT"),
          t("ADDRESS_REPEATING_LETTERS"),
          t("ADDRESS_TOO_MANY_SPECIAL_CHARS")
        );
        break;
    }
  }
  
  // Form-type specific validations (shipping)
  if (formType === "shipping") {
    switch (fieldName) {
      case "cityShipping":
        error = validateCityField(
          value as string,
          locale,
          t("CITY_EMPTY"),
          t("CITY_INVALID")
        );
        break;
        
      case "stateNameShipping":
        error = validateName(
          value as string,
          t("STATE_EMPTY"),
          t("STATE_INVALID")
        );
        break;
        
      case "zipShipping":
        error = validatePostalCode(
          value as string,
          locale,
          formShippingAddress.countryValue,
          t("ZIP_CODE_EMPTY"),
          t("ZIP_CODE_INVALID"),
          t("REGION_NOT_SUPPORTED")
        );
        break;
        
      case "countryShipping":
        error = validateName(
          value as string,
          t("COUNTRY_REQUIRED"),
          t("COUNTRY_INVALID")
        );
        break;
        
      case "addressShipping":
        error = validateAddressField(
          value as string,
          locale,
          t("ADDRESS_REQUIRED"),
          t("ADDRESS_INVALID"),
          t("ADDRESS_PO_BOX"),
          t("ADDRESS_TOO_SHORT"),
          t("ADDRESS_REPEATING_LETTERS"),
          t("ADDRESS_TOO_MANY_SPECIAL_CHARS")
        );
        break;
    }
  }
  
  return error;
};

